<template>
    <div>
        <div class="row mb-3">
            <div class="col-auto" v-if="journey">
                <navigate-to-actionable-report-link 
                    :journey-id="journey.id"
                    class="btn btn-outline-secondary"
                />
            </div>
            <div class="col-auto">
                <a class="btn btn-outline-secondary" href="/JourneyBuilder/Overview">
                    <i class="fas fa-route"></i> {{resource('JourneyBuilder_Index_Overview_Button_Text')}}
                </a>
            </div>
        </div>
        <bootstrap-box>
            <template #body>
                <template v-if="journey">
                    <div class="row">
                        <div class="col-3 menu-container" :class="{ 'collapsed': !showMenu }">
                            <toggle-button v-model="showMenu"
                                           :sync="true"
                                           :labels="{checked: resource('Bool_Expand'), unchecked: resource('Bool_Collapse')}"
                                           :width="78"

                                           class="menu-toggle"
                            />

                            <div class="menu-wrapper">
                                <menu-area
                                    :journey-id="journey.id"
                                    :journey-name.sync="journey.name"
                                    :journey-world.sync="journey.worldId"
                                    :journey-type.sync="journey.type"
                                    :journey-description.sync="journey.description"
                                    :journey-start-date.sync="journey.startDate"
                                    :journey-end-date.sync="journey.endDate"
                                    :journey-is-active.sync="journey.isActive"
                                    :journey-division-id.sync="journey.divisionId"

                                    v-on:update:journeyStartDate="setStartDate($event)"
                                    v-on:update:journeyEndDate="setEndDate($event)"

                                    @save="publishJourney"
                                    @clone="cloneJourney(journey.id)"
                                    @add-brick="addBrickToJourney"
                                >
                                </menu-area>
                            </div>
                        </div>

                        <div class="col">
                            <canvas-area
                                ref="composer"

                                :bricks="journey.bricks"
                                :journeyType="journey.type"

                                @top-left-changed="setNewBrickDefaultPosition($event.x, $event.y)"

                                @brick-moved="brickMoved($event.brickId, $event.x, $event.y)"
                                @connector-moved="connectorMoved($event.connectionId, $event.sourceBrickId, $event.connectorX, $event.connectorY)"

                                @add-brick-at-location="addBrickAtLocation"
                                @delete="deleteBrick($event.brick.id, $event.requiresPrompt)"

                                @add-connection="addConnectionToBrick($event.id)"
                                @settings-changed="updateSettings($event.id, $event.config, $event.name)"
                                @connection-created="connectBricks($event.connectionId, $event.sourceBrickId, $event.destinationBrickId)"
                                @change-connection="changeConnection($event.sourceBrickId, $event.connectionId, $event.model)"
                                @delete-connection-checkpoint="deleteCheckpoint($event.connection, $event.checkpoint)"
                                @delete-connection="deleteConnection($event.sourceBrickId, $event.destinationBrickId, $event.connectionId)"
                            >
                            </canvas-area>
                            <!--
                            TODO: handle event? 
                            @connection-active="setConnectorActive($event.connectionId, $event.sourceBrickId, $event.active)"
                            -->
                        </div>
                    </div>
                </template>
                <loading-box v-else />
            </template>
        </bootstrap-box>

        <delete-brick-prompt 
            v-if="brickDeletePrompt.show"
            :brick-id="brickDeletePrompt.brickId"
            :title="resource('JourneyBuilder_Index_DeleteBrick_ConfirmationTitle')"
            :body="resource('JourneyBuilder_Index_DeleteBrick_ConfirmationBody')"
            @yes="deleteBrick(brickDeletePrompt.brickId, false)"
            @hidden="brickDeletePrompt.show = false"
        />
    </div>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';
    import _ from 'lodash';
    
    import api from '@modules/JourneyBuilder/api';
    import createJourneyBrickFactory from '@modules/JourneyBuilder/Bricks/journey-brick-factory';
    import dateHelpers from '@modules/JourneyBuilder/Util/date-helpers';
    
    import {
        bootstrapDangerGrowl as errorGrowl,
        bootstrapSuccessGrowl as successGrowl
    } from "@core/Util/growler";

    import snowplow from '@modules/JourneyBuilder/snowplow';
    import { brickDefinitions } from '@modules/JourneyBuilder/Bricks/brick-definitions';
    import brickTypes from '@modules/JourneyBuilder/Bricks/brick-types';
    import {promptV2 as prompt} from "@core/Util/prompt";
    import {sprintf} from 'sprintf';
    import {getCurrentUser} from "@core/Util/current-user";

    import resourceMixin from "@core/Util/Mixins/resources";
    import cloneJourneyMixin from '@modules/JourneyBuilder/Mixins/clone-journey';
    import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';
    
    import BootstrapBox from "@core/Components/Util/BootstrapBox.vue";
    import MenuArea from './MenuArea';
    import CanvasArea from "./CanvasArea";
    import { ToggleButton } from 'vue-js-toggle-button';
    import DeleteBrickPrompt from "@modules/JourneyBuilder/Components/Composer/DeleteBrickPrompt";
    import LoadingBox from "@core/Components/Util/LoadingBox";
    import {NavigateToActionableReportLink} from "@modules/JourneyBuilder/Components/Elements";
    import { checkpointDefinitionsArray, checkpointDefinitions } from '@modules/JourneyBuilder/Bricks/checkpoint-definitions';

    const {mapState, mapMutations, mapActions} = createNamespacedHelpers('journeyBuilder/composer');

    export default {
        name: 'index',
        components: {
            LoadingBox,
            BootstrapBox,
            MenuArea,
            CanvasArea,
            ToggleButton,
            DeleteBrickPrompt,
            NavigateToActionableReportLink
        },
        mixins: [resourceMixin, cloneJourneyMixin, permissionsMixin],
        
        async created() {
            const vm = this;
            
            //An empty journey provides a definition for our properties/ensures the UI does not crash
            let journeyAwaitable = new Promise((resolve) => resolve({
                id: null,
                name: '',
                worldId: '',
                type: 0,
                description: '',
                isActive: false,
                bricks: [],
                divisionId: getCurrentUser().DivisionId,
                startDate: dateHelpers.getStringifiedDate(new Date()),
                config: {}
            }));

            const loadConditionsProm = vm.loadAvailableConnectionConditions();
            
            if(vm.id){
                journeyAwaitable = api.getJourneyById(vm.id);
            }
            
            try {
                const brickDefs = await api.getJourneyBrickDefinitions();

                const typeConfigMapping = brickDefs.reduce((map, brickDef) => {
                    map[brickDef.type] = JSON.parse(brickDef.config);
                    return map;
                }, {});

                await loadConditionsProm;
                vm.journeyBrickFactory = createJourneyBrickFactory(typeConfigMapping);
                
                //A brickFactory is required before we can initialise our journey
                vm.setJourney(
                    await journeyAwaitable
                );
                
                if(vm.id){
                    await this.loadBrickCommentIndicators();
                }
            } catch(err) {
                errorGrowl(err);
            }
            
            window.addEventListener('beforeunload', evt => {
                if(vm.canWrite && vm.wasChangedAfterLastSave){
                    //According to the spec, preventDefault should be the go to way to prompt the user
                    //this is not implemented by all browsers yet however (Chrome)
                    //thus we'll also have to set the 'returnValue' property to ensure our handler
                    //works in all browsers
                    //the actual value of 'returnValue' is ignored by modern browsers
                    evt.preventDefault();
                    evt.returnValue = vm.resource('JourneyBuilder_UnsavedChanges');
                }
            });
        },
        
        methods: {
            ...mapMutations(['addBrickToMap', 'updateBrickMapProperty', 'removeBrickFromMap', 'resetComments', 'setNewComment', 'setJourneyId']),
            ...mapActions(['setBrickMap', 'loadAvailableConnectionConditions']),
            ...mapActions({
                async loadBrickCommentIndicators(dispatch){
                    try {
                        await dispatch('loadBrickCommentIndicators', this.journey.id);
                    } catch(e) {
                        errorGrowl(e);
                    }
                }
            }),
            setStartDate(date){
                this.journey.startDate = date;
            },
            setEndDate(date){
                this.journey.endDate = date;
            },
            setJourney(journey){
                const vm = this;
                
                //We should normally not invoke mixin privates directly
                vm.$_setLockedState(true);
                
                const brickModels = journey.bricks;
                
                //Explicit brick creation gives us more flexibility regarding funcs/properties
                //journey.bricks = brickModels.map(brickModel => vm.journeyBrickFactory.createJourneyBrickFromModel(brickModel));

                const bricks =  brickModels.map(brickModel => vm.journeyBrickFactory.createJourneyBrickFromModel(brickModel));

                const brickMap = bricks.reduce((brickMap, brick) => {
                    brickMap[brick.id] = brick;
                    return brickMap;
                }, {});
                
                vm.setBrickMap(bricks);
                journey.bricks = bricks;
                   
                // Once all bricks have been created; we can start completing our connections
                brickModels.forEach(brickModel => {
                    const brick = brickMap[brickModel.id];

                    brick.outgoingConnections.forEach(connection => {
                        const modelConnection = brickModel.connections.find(c => c.id === connection.id);
                        if(modelConnection && modelConnection.destinationBrickId){
                            connection.destinationBrick = brickMap[modelConnection.destinationBrickId];
                        }
                    });
                });

                vm.journey = journey;
                
                //We should only start watching our journey post-load
                //this to prevent accidentally triggering an 'altered' state on load
                this.$watch(
                    'journey', 
                    () => vm.wasChangedAfterLastSave = true,
                    { deep: true }
                );
            },
            async getPublishJourneyPrompt(){
                const vm = this;
                
                if(!vm.journey.bricks.some(b => b.type === brickTypes.goal && b.isActive())) {
                    await prompt(
                        vm.resource('JourneyBuilder_Index_PublishJourney_ConfirmationTitle'),
                        vm.resource('JourneyBuilder_Index_PublishJourney_WithoutGoalBrick_ConfirmationBody')
                    )
                }
                
                //An inactive journey does not need a prompt prior to saving
                if(!vm.journey.isActive){
                    return;
                }

                const promptText = vm.journey.startDate ?
                    sprintf(vm.resource('JourneyBuilder_Index_PublishJourney_WithStartDate_ConfirmationBody'), vm.journey.startDate) :
                    vm.resource('JourneyBuilder_Index_PublishJourney_WithoutStartDate_ConfirmationBody');
                
                await prompt(vm.resource('JourneyBuilder_Index_PublishJourney_ConfirmationTitle'), promptText);
            },
            async publishJourney(){
                const vm = this;
                const isNewJourney = vm.journey.id === null;
                
                snowplow.trackStructuredEvent('publish-journey', vm.journey.id);

                try {
                    await vm.getPublishJourneyPrompt()    
                } catch(e){
                    //Cancelled
                    return;
                }
                
                try {
                    //Make a deeplcone so we can safely remove all non-configured checkpoints
                    let journey = _.cloneDeep(vm.journey);

                    journey.bricks.forEach(brick => {
                        brick.outgoingConnections.forEach(connection => {
                            checkpointDefinitionsArray.forEach(checkpointDefinition => {
                                if(checkpointDefinition.isActiveForConnection(connection) && !checkpointDefinition.isConfiguredForConnection(connection)) {
                                    checkpointDefinition.deactivateForConnection(connection);
                                }
                            })
                        });
                    });
                    
                    journey = await api.publishJourney(journey, vm.newComments);

                    successGrowl(vm.resource('JourneyBuilder_Index_JourneyPublished_SuccessMessage'));

                    //Upon creation of a new journey; we want to reflect the newly created journey in our URL
                    //this makes for easier sharing of new journeys/no accidental loss of current journey upon page reload
                    if(isNewJourney){
                        //We only need the id of our newly created journey, everything else can remain as is
                        //this approach also ensures we don't lose any potential unconnected endpoints
                        vm.journey.id = journey.id;
                        vm.reflectCurrentJourneyInBrowserUrl();
                    }

                    //Upon saving; all bricks can be considered not-new, enabling a new set of features for the given brick
                    vm.journey.bricks.forEach(brick => {
                        vm.updateBrickMapProperty({ brickId: brick.id, property: 'isNew', value: false });
                        brick.outgoingConnections.forEach(connection => connection.isNew = false);
                    });

                    this.resetComments();

                    //Disable confirmation popup on page leave
                    vm.wasChangedAfterLastSave = false;

                    //Include newly created comments in our brick comments
                    await this.loadBrickCommentIndicators();
                } catch(e){
                    errorGrowl(e);
                }
            },
            setNewBrickDefaultPosition(x, y){
                this.brickDefaultX = x;
                this.brickDefaultY = y;
            },
            addBrickAtLocation(info){
                const brickDefinition = info.brickDefinition;
                const location = info.location;
                
                const brick = this.addBrickToJourney(brickDefinition);
                brick.x = location.x;
                brick.y = location.y;
            },
            addBrickToJourney(brickDefinition){
                const vm = this;
                snowplow.trackStructuredEvent('add-brick', brickDefinition.type);
                
                const brick = vm.journeyBrickFactory.createNewJourneyBrick(brickDefinition.type);
                
                //Add 15px to ensure endpoint visibility
                brick.x = vm.brickDefaultX + 15;
                brick.y = vm.brickDefaultY + 15;
                
                vm.addBrickToMap(brick);
                this.journey.bricks.push(brick);
                
                return brick;
            },
            addConnectionToBrick(brickId){
                snowplow.trackStructuredEvent('add-connection', brickId);
                
                const brick = this.journey.bricks.find(b => b.id === brickId);
                brick.addConnection();
            },
            deleteBrick(brickId, requiresPrompt) {
                snowplow.trackStructuredEvent('delete-brick', brickId);

                const vm = this;
                
                if(requiresPrompt){
                    this.brickDeletePrompt.show = true;
                    this.brickDeletePrompt.brickId = brickId;
                    return;
                }

                const brick = vm.journey.bricks.find(b => b.id === brickId);

                //Create a copy as to not cause crashes/weird behavior due to the connections
                //being edited in the deleteConnection(...) function 
                [...brick.connections].forEach(connection => connection.delete());

                vm.journey.bricks.splice(vm.journey.bricks.indexOf(brick), 1);
                this.removeBrickFromMap(brick.id);
            },
            updateSettings(brickId, config, name) {
                const brick = this.journey.bricks.find(b => b.id === brickId);
                
                brick.config = config || {};
                brick.name = name || brickDefinitions[brick.type].name;
            },
            updateConnection(connectionId, config) {
                for(const brick of this.journey.bricks) {
                    let conn = brick.connections.find(c => c.id === connectionId);
                    if (conn) {
                        conn.config = Object.assign(conn.config, config.settings);
                    }
                }
            },
            connectBricks(connectionId, sourceBrickId, destinationBrickId){
                snowplow.trackStructuredEvent('connect-bricks', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));

                const sourceBrick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const destinationBrick = this.journey.bricks.find(b => b.id === destinationBrickId);
                
                const connection = sourceBrick.connections.find(r => r.id === connectionId);
                connection.destinationBrick = destinationBrick;
            },
            brickMoved(brickId, x, y){
                const brick = this.journey.bricks.find(b => b.id === brickId);
                
                brick.x = x;
                brick.y = y;
            },
            connectorMoved(connectionId, sourceBrickId, connectorX, connectorY){
                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);
                
                connection.connectorX = connectorX;
                connection.connectorY = connectorY;
            },
            changeStatus(connectionId, sourceBrickId, model) {                
                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);

                connection.config = { ...connection.config, ...model };
            },
            changeConnection(sourceBrickId, connectionId, model) {
                snowplow.trackStructuredEvent('change-connection', model.id);

                const brick = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = brick.connections.find(c => c.id === connectionId);
                connection.config = { ...connection.config, ...model };
            },
            deleteConnection(sourceBrickId, destinationBrickId, connectionId) {
                snowplow.trackStructuredEvent('delete-connection', connectionId, JSON.stringify({sourceBrickId, destinationBrickId}));

                const source = this.journey.bricks.find(b => b.id === sourceBrickId);
                const connection = source.connections.find(c => c.id === connectionId);

                connection.delete();
            },
            deleteCheckpoint(connection, checkpoint) {
                const source = this.journey.bricks.find(b => b.id === connection.sourceBrickId);
                const conn = source.connections.find(c => c.id === connection.id);

                checkpointDefinitions[checkpoint].deactivateForConnection(conn);
            },
            reflectCurrentJourneyInBrowserUrl(){
                const vm = this;
                
                if(window.history && window.history.pushState){
                    window.history.pushState(
                        null,
                        document.title,
                        api.getViewJourneyUrl(vm.journey.id)
                    )
                }
            }
        },
        watch: {
            // journey(value){
            //     this.providedJourney.value = value;
            // },
            'journey.id'(value){
                //Allow components to access our journey
                this.setJourneyId(value);
            }
        },
        computed: {
            ...mapState(['newComments', 'finishedInitialRenderCycle']),
        },
        data(){
            return {
                showMenu: true,
                
                journey: null,
                // providedJourney: {
                //     value: null,
                // },
                //Object definition; overwritten in Create(...) handler
                journeyBrickFactory: {
                    createJourneyBrick: (type) => null,
                    createJourneyBrickFromModel: (remoteModel) => null
                },
                brickDefaultX: 0,
                brickDefaultY: 0,
                
                wasChangedAfterLastSave: false,

                brickDeletePrompt: {
                    show: false,
                    brickId: null
                }
            }
        },
        provide(){
            const vm = this;
            
            return {
                providedJourney: {
                    get value(){
                        return vm.journey;
                    }
                }
            }
        },
        props: {
            id: {
                required: false,
                type: String
            }
        }
    }
</script>

<style  scoped lang="scss">
	@import "~@modules/JourneyBuilder/Style/journey-builder-index.scss";
</style>
